import React, { useCallback, useEffect, useState, ReactNode } from "react";

export type WindowContextProps = {
  clientHeight: number;
  clientWidth: number;
  clientDpr: number;
};

export const WindowContext = React.createContext<WindowContextProps>({
  clientHeight: 500,
  clientWidth: 500,
  clientDpr: 1,
});

type WindowContextProviderProps = {
  children: ReactNode;
};

export const WindowContextProvider: React.FC<WindowContextProviderProps> = ({
  children,
}) => {
  const getVh = useCallback(() => {
    return Math.max(
      document.documentElement.clientHeight || 0,
      window.innerHeight || 0,
      document.body.offsetHeight,
    );
  }, []);

  const getVw = useCallback(() => {
    return Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0,
    );
  }, []);

  const getVdpr = useCallback((): number => {
    return window.devicePixelRatio || 1;
  }, []);

  const [clientHeight, setVh] = useState<number>(getVh());
  const [clientWidth, setVw] = useState<number>(getVw());
  const [clientDpr, setDpr] = useState<number>(getVdpr());

  useEffect(() => {
    setVh(getVh());
    setVw(getVw());
    setDpr(getVdpr());

    const handleResize = () => {
      setVh(getVh());
      setVw(getVw());
      setDpr(getVdpr());
    };

    const handleDprChange = (e: MediaQueryListEvent) => {
      setDpr(e.matches ? window.devicePixelRatio : 1);
    };

    window.addEventListener("resize", handleResize);

    const dprMediaQuery = window.matchMedia(
      `(resolution: ${window.devicePixelRatio}dppx)`,
    );
    dprMediaQuery.addEventListener("change", handleDprChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      dprMediaQuery.removeEventListener("change", handleDprChange);
    };
  }, [getVh, getVw, getVdpr]);

  return (
    <WindowContext.Provider value={{ clientHeight, clientWidth, clientDpr }}>
      {children}
    </WindowContext.Provider>
  );
};
